interface RenderOptions {
  app: { innerHTML: string };
  brand: string;
  menu: string;
}

interface SoftwareVersionFeature {
  title: string;
  description: string;
}

interface SoftwareVersionEvidence {
  value: string;
  label: string;
}

export interface SoftwareVersionRecord {
  version: string;
  status: string;
  statusTone: "preview" | "stable" | "foundation";
  title: string;
  displayDate: string;
  intro: string;
  metadata: Array<{ label: string; value: string }>;
  features: SoftwareVersionFeature[];
  evidence?: SoftwareVersionEvidence[];
  openByDefault?: boolean;
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;",
  })[character] ?? character);

export const softwareVersions: SoftwareVersionRecord[] = [
  {
    version: "2A",
    status: "Production shadow rollout",
    statusTone: "preview",
    title: "Nova transcription and Marker accuracy release",
    displayDate: "Oct 8, 2026",
    intro: "Version 2A introduces Cloudflare Nova-3 audio evidence, overlapping Whisper transcription, and measured shadow evaluation to improve transcription timing and future Marker placement without changing confirmed Markers.",
    metadata: [
      { label: "Release date", value: "Oct 8, 2026" },
      { label: "Release revision", value: "Production shadow rollout" },
      { label: "Application build", value: "Version 2A" },
      { label: "Database schema", value: "0049" },
    ],
    features: [
      {
        title: "Software version history",
        description: "Built-in version and revision records with release dates, features, fixes, and release evidence.",
      },
      {
        title: "Marker accuracy baseline",
        description: "Confirmation feedback becomes an accuracy report with error percentiles, tolerance bands, and Service Type and Plan Item breakdowns.",
      },
      {
        title: "Transcription and audio evidence",
        description: "Cloudflare Nova-3 adds word timing, speech intervals, candidate silence, and anonymous speaker changes while overlapping Whisper chunks improve boundary timestamps; music classification remains deferred.",
      },
      {
        title: "Shadow-tested Marker reasoning",
        description: "Nova evidence feeds a global order-aware Marker solver and bounded reasoning pass in shadow evaluation. Confirmed and visible production Markers remain unchanged until the activation gate is passed and separately approved.",
      },
    ],
  },
  {
    version: "1B",
    status: "Production stable",
    statusTone: "stable",
    title: "Intelligent media release",
    displayDate: "Oct 8, 2026",
    intro: "Completes the production DVR foundation and adds searchable transcripts, intelligent service-plan markers, verified archive and restore, and full operational controls.",
    metadata: [
      { label: "Release date", value: "Oct 8, 2026" },
      { label: "Release revision", value: "4c7d5b3" },
      { label: "Application build", value: "ceb98d6" },
      { label: "Database schema", value: "0045" },
    ],
    features: [
      {
        title: "Service scheduling and capture",
        description: "Planning Center-linked services, recurring and one-time schedules, Eastern-time handling, source recordings, and Event-level clips.",
      },
      {
        title: "Video library and playback",
        description: "Scoped discovery, filters, thumbnails, direct uploads, timestamp links, Cloudflare playback, and restored archive playback.",
      },
      {
        title: "Transcription and smart markers",
        description: "Asynchronous transcripts, full-text search, assisted markers, confidence review, and human confirmation.",
      },
      {
        title: "Archive, restore, and retention",
        description: "Verified Drive archives, safe Cloudflare retention, restore workflows, protection policies, and deletion safeguards.",
      },
      {
        title: "Access and administration",
        description: "Planning Center identity, DVR roles and Service Type scope, invitations, user administration, and audit history.",
      },
      {
        title: "Production hardening",
        description: "Workflow retries, provider reconciliation, archive drills, bounded logging, migration integrity, and daylight-saving tests.",
      },
    ],
    evidence: [
      { value: "479", label: "automated tests passed" },
      { value: "45", label: "database migrations applied" },
      { value: "11,912", label: "transcript segments indexed" },
      { value: "0", label: "failed workflows at release" },
    ],
    openByDefault: true,
  },
  {
    version: "1A",
    status: "Foundation",
    statusTone: "foundation",
    title: "Launch-critical DVR baseline",
    displayDate: "Aug 31, 2026",
    intro: "Established the secure DVR foundation: service scheduling, continuous recording capture, Event clipping, scoped playback, verified archive and restore, and core administration.",
    metadata: [
      { label: "Baseline date", value: "Aug 31, 2026" },
      { label: "Release revision", value: "949f1e8" },
      { label: "Release milestone", value: "Verified baseline" },
      { label: "Database schema", value: "0015" },
    ],
    features: [
      {
        title: "Secure access",
        description: "Planning Center login, pending and active users, server-enforced roles, and Service Type scope.",
      },
      {
        title: "Events and schedules",
        description: "Recurring schedules, one-time Events, Eastern-time generation, and daylight-saving boundary coverage.",
      },
      {
        title: "Capture and clipping",
        description: "Continuous Resi ingest, DVR-controlled recording segmentation, discovery, and Event clip creation.",
      },
      {
        title: "Library and player",
        description: "Authorized video discovery, live viewing, playback controls, metadata, and clear processing states.",
      },
      {
        title: "Archive and retention",
        description: "Verified Shared Drive archives, 90-day online retention, restore-to-playback, and protection overrides.",
      },
      {
        title: "Administration and recovery",
        description: "Service configuration, user management, workflow status and retries, branding, and audit logging.",
      },
    ],
    evidence: [
      { value: "15", label: "database migrations applied" },
      { value: "4", label: "launch schedules covered" },
      { value: "3", label: "Sunday service clips supported" },
      { value: "Passed", label: "full local multi-runtime proof" },
    ],
  },
];

const metadataMarkup = (record: SoftwareVersionRecord): string => `
  <dl class="software-version-metadata">
    ${record.metadata.map((item) => `<div><dt>${escapeHtml(item.label)}</dt><dd>${escapeHtml(item.value)}</dd></div>`).join("")}
  </dl>`;

const featureMarkup = (record: SoftwareVersionRecord): string => `
  <div class="software-version-section-heading">
    <h2>Features &amp; Fixes</h2>
    <span>${record.statusTone === "preview" ? "Planned scope" : `Included in Version ${escapeHtml(record.version)}`}</span>
  </div>
  <section class="software-version-features" aria-label="Version ${escapeHtml(record.version)} features and fixes">
    ${record.features.map((feature, index) => `<article>
      <span class="software-version-feature-number" aria-hidden="true">${String(index + 1).padStart(2, "0")}</span>
      <div><h3>${escapeHtml(feature.title)}</h3><p>${escapeHtml(feature.description)}</p></div>
    </article>`).join("")}
  </section>`;

const evidenceMarkup = (record: SoftwareVersionRecord): string => record.evidence?.length ? `
  <div class="software-version-section-heading">
    <h2>Release evidence</h2>
    <span>${record.statusTone === "stable" ? "Recorded at release" : "Verified baseline"}</span>
  </div>
  <section class="software-version-evidence" aria-label="Version ${escapeHtml(record.version)} release evidence">
    ${record.evidence.map((item) => `<div><strong>${escapeHtml(item.value)}</strong><span>${escapeHtml(item.label)}</span></div>`).join("")}
  </section>` : "";

const versionMarkup = (record: SoftwareVersionRecord): string => `
  <details class="software-version" data-version="${escapeHtml(record.version)}" ${record.openByDefault ? "open" : ""}>
    <summary class="software-version-summary">
      <span class="software-version-summary-copy">
        <strong>Version ${escapeHtml(record.version)}</strong>
        <span class="software-version-status ${escapeHtml(record.statusTone)}"><i aria-hidden="true"></i>${escapeHtml(record.status)}</span>
        <small>${escapeHtml(record.title)}</small>
      </span>
      <span class="software-version-summary-meta"><time>${escapeHtml(record.displayDate)}</time><i class="software-version-chevron" aria-hidden="true">⌄</i></span>
    </summary>
    <div class="software-version-body">
      <p class="software-version-intro">${escapeHtml(record.intro)}</p>
      ${metadataMarkup(record)}
      ${featureMarkup(record)}
      ${evidenceMarkup(record)}
    </div>
  </details>`;

const aboutPageContentMarkup = (): string => `
    <section class="about-platform" aria-labelledby="about-heading">
      <h1 id="about-heading">About</h1>
      <div class="about-sections-container">
        <section class="about-platform-overview" aria-labelledby="about-platform-heading">
          <h2 id="about-platform-heading">The Platform</h2>
          <p class="about-platform-capabilities">Friends DVR Platform makes it easier to capture church services as part of an existing workflow through scheduled Resi RTMP streams, turn them into individual Event videos, and find them later in a searchable library. Its API integration with Planning Center keeps sign-in, service details, Plan Items, and user access connected. Transcripts and smart Markers help users jump to the precise moments they need to review. Recordings are processed on Cloudflare, then moved safely to Google Drive for low-cost, long-term archival storage.</p>
        </section>
        <section class="about-creation" aria-labelledby="about-creation-heading">
          <h2 id="about-creation-heading">The Creation Story</h2>
          <p class="about-platform-story">The DVR Platform grew from a practical need: Friends Church needed a dependable way to access recordings of its church services for review and long-term archiving. Jared Jones, Sam Jennings, and Tanner Perkins shaped the original idea during an early brainstorming session about how the church could capture, organize, and retrieve its services more effectively. Tanner helped with the early setup and testing of video streams, while Jared led the extensive software development that transformed those first ideas into the platform used today. The result is a purpose-built system created around the church’s real production, review, and archival workflows.</p>
        </section>
      </div>
    </section>
    <section class="about-versions" aria-labelledby="about-releases-heading">
      <header class="about-versions-heading">
        <h2 id="about-releases-heading">Releases</h2>
      </header>
      <div class="software-version-list" aria-label="Software version history">
        ${softwareVersions.map(versionMarkup).join("")}
      </div>
    </section>`;

export const aboutPageMarkup = (): string => `
  <main class="shell about-page-shell">
    ${aboutPageContentMarkup()}
  </main>`;

export const renderAboutPage = ({ app, brand, menu }: RenderOptions): void => {
  app.innerHTML = `
    <main class="shell about-page-shell">
      <header class="topbar page-title-topbar">${brand}<div class="mobile-page-heading" aria-hidden="true"><h1>About</h1></div>${menu}</header>
      ${aboutPageContentMarkup()}
    </main>`;
};
